<template>
  <div class="alerts-component screen">
    <SheetVehicleFeatureHeader title="Alerts" v-if="$isMobile" />
    <div class="header-band" v-else>
      <VehicleModalMenu @close="beforeClose()" />
      <span class="title">Alerts</span>
      <div class="divider"></div>
      <VehiclePickerMenu />
      <Tooltip class="ns-tooltip" @click.native="showTooltip" />
      <CloseButton inline @click="beforeClose()" />
    </div>
    <Alert />
    <div class="heading">Alert search</div>
    <div class="search-top">
      <div class="date-pickers">
        <DatePicker label="FROM" v-model="fromDate" />
        <DatePicker label="TO" :minDate="fromDate" v-model="toDate" />
      </div>
      <div class="buttons">
        <button class="btn btn-outline search-btn mr-3" :disabled="processing" @click="getAlertsByDate()">
          <span v-show="!processing">Search</span>
          <Spinner v-show="processing" />
        </button>
        <button class="btn btn-outline manage-btn" @click="showModal('manage-alerts')">Manage alerts</button>
      </div>
    </div>
    <div class="search-results">
      <div class="search-results-heading" v-if="searchResults">
        {{ alerts.length }} Alerts between <span>{{ fromDate }}</span> and
        <span>{{ toDate }}</span>
      </div>
      <div class="search-results-heading" v-else>Last 10 Alerts</div>
      <div class="search-results-content">
        <PanelSpinner v-if="loading" />
        <SmoothReflow>
          <template v-if="!loading">
            <template v-if="alerts.length">
              <div class="item container" :class="{ active: item.Id === activeAlertId }" v-for="item in alerts" :key="item.Id">
                <div class="row align-items-center" @click="displayAlertOnMap(item.Id, item)">
                  <div class="col-lg-4 d-flex align-items-center mb-3 mb-lg-0">
                    <img :src="getAlertIconURL(item.Category)" class="alert-icon" alt />
                    <span class="alert-name">{{ item.Name }}</span>
                  </div>
                  <div class="geo-address col-lg-4 mb-0 mb-md-2 mb-lg-0 text-left">
                    <span>{{ item.Location.GeoAddress }}</span>
                  </div>
                  <div class="col-lg-4 pl-lg-0 mb-2 mb-lg-0 d-flex justify-content-lg-start">
                    <div class="datetime d-flex d-lg-block flex-grow-1 flex-lg-grow-0 justify-content-md-evenly">
                      <div class="date mr-3 mr-lg-0">
                        <img src="~@/assets/images/calendar.svg" width="17" height="17" alt />
                        <span>{{ getDate(item.Location.DateTime) }}</span>
                      </div>
                      <div class="time">
                        <img src="~@/assets/images/clock.svg" width="17" height="17" alt />
                        <span>{{ getTime(item.Location.DateTime) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <ErrorBox message="No Alerts Received" v-else />
          </template>
        </SmoothReflow>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "~@/assets/scss/variables.scss";

.alerts-component {
  .header-band {
    display: flex;
    align-items: center;
    background-color: #fff;
    padding: 12px 30px;
    margin: -10px -30px 10px -30px;
    color: $brand-color-1;
    position: relative;
    font-size: 19px;

    .title {
      font-weight: 600;
      color: $brand-color-1;
      margin-left: 5px;
    }

    /deep/ .menu {
      .menu__link {
        font-weight: 300;
      }
    }

    .divider {
      width: 1px;
      height: 20px;
      background-color: $brand-color-1;
      margin: 0 15px;
    }

    .ns-tooltip {
      margin-left: auto;
      margin-right: 5px;
    }

    /deep/ .close-button {
      margin-left: 0;
    }

    /deep/ .menu__link {
      color: $brand-color-1;
    }

    /deep/ .menu-button .icon {
      fill: $brand-color-1;
    }

  }

  > .heading {
    font-size: 16px;
    color: $brand-color-2;
    margin-bottom: 10px;
  }

  .search-top {
    display: flex;
    align-items: center;
    margin-bottom: 15px;

    .date-pickers {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
    }

    .buttons {
      display: flex;

      .search-btn,
      .manage-btn {
        min-width: 92px;
        font-size: 14px;
      }
    }
  }

  &.small {
    .search-top {
      .buttons {
        margin: 15px 0 20px 0;
      }
    }
  }

  .search-results {
    &-heading {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 5px;

      span {
        color: $brand-color-2;
      }
    }

    &-content {
      background: $brand-color-4;
      padding: 20px;

      .item {
        background: #fff;
        padding: 0;
        margin: 0 0 10px 0;

        &:last-child {
          margin-bottom: 0;
        }
        border: 2px solid #fff;
        box-shadow: $drop-shadow-light;

        &:hover {
          cursor: pointer;
        }

        &.active {
          border: 2px solid $red;
        }

        .row > [class*="col-lg-4"] {
          border-right: none;
          padding: 3px;
          position: relative;

          &:after {
            content: "";
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            height: 40px;
            border-right: 1px solid $brand-color-3;
          }

          &:nth-child(2):after {
            right: -10px;
          }

          &:last-child {
            &:after {
              display: none;
            }
          }
        }

        .alert {
          &-icon {
            width: 32px;
            height: 32px;
            margin-right: 9px;
            margin-left: 20px;
            background: transparent;
          }

          &-name {
            position: relative;
            font-weight: 600;
            padding-left: 13px;

            &:before {
              content: "";
              position: absolute;
              top: 50%;
              left: 0;
              height: 40px;
              transform: translateY(-50%);
              border-left: 1px solid $brand-color-3;
            }
          }
        }

        .date {
          line-height: 16px;
          margin-bottom: 5px;

          img {
            vertical-align: top;
            margin-right: 8px;
          }
        }

        .time {
          line-height: 16px;

          img {
            vertical-align: top;
            margin-right: 8px;
          }
        }

        .geo-address {
          padding-left: 10px !important;
          padding-right: 12px;
        }

        .datetime {
          padding: 10px;
          margin-left: 12px;
        }

        .delete-icon {
          fill: $brand-color-1;
        }

        .button {
          &:hover {
            cursor: pointer;
          }
        }
      }
    }
  }
}

@media (max-width: $md-1) {
  .alerts-component {
    > .heading {
      margin: 0 0 10px 0;
    }

    .search-top {
      display: block;

      .buttons {
        display: block;
        margin: 15px 0 20px 0;
      }
    }

    .search-results-content {
      .item {
        .row > [class*="col-lg-4"] {
          border-right: none;

          &:after {
            display: none;
          }
        }

        .alert-icon {
          margin-top: 5px;
        }

        .alert-name {
          padding-left: 10px;
          margin-top: 5px;

          &:before {
            display: none;
          }
        }

        .geo-address {
          padding-left: 27px !important;

          span {
            padding-left: 10px;
            border-left: 1px solid $brand-color-2;
            display: block;
          }
        }

        .datetime {
          padding-left: 11px;
          padding-top: 13px;
          margin-top: -6px;
          border-left: 1px solid $brand-color-2;
          margin-left: 24px;

          .date {
            margin-bottom: 0;
          }
        }
      }
    }
  }
}
</style>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import moment from "moment";
import { assetService, modalService, tooltipService } from "@/services";
import SheetVehicleFeatureHeader from "@/components/screens/SheetVehicleFeatureHeader";
import DatePicker from "@/components/DatePicker";
import VehicleModalMenu from "@/components/screens/VehicleModalMenu";
import VehiclePickerMenu from "@/components/screens/VehiclePickerMenu";
import CloseButton from "@/components/CloseButton";
import Tooltip from "@/components/Tooltip";

export default {
  name: "Alerts",
  components: {
    VehicleModalMenu,
    VehiclePickerMenu,
    CloseButton,
    Tooltip,
    SheetVehicleFeatureHeader,
    DatePicker,
  },
  data() {
    return {
      loading: true,
      processing: false,
      searchResults: false,
      submitSuccess: false,
      submitError: false,
      activeAlertId: null,
      alerts: [],
      fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    };
  },
  computed: {
    ...mapState(["map"]),
    ...mapGetters("asset", ["getActiveSafeAndSoundVehicleId"]),
    activeAssetId() {
      return this.getActiveSafeAndSoundVehicleId();
    },
  },
  created() {
    const vehicleId = this.getActiveSafeAndSoundVehicleId();
    const startTime = Date.now();
    assetService
      .getVehicleAlertsHistory(vehicleId)
      .then((data) => {
        const timeElapsed = Date.now() - startTime;
        setTimeout(
          () => {
            this.alerts = data;
            this.loading = false;
          },
          timeElapsed < 500 ? 500 - timeElapsed : 0
        );
      })
      .catch((error) => {
        this.loading = false;
      });
  },
  mounted() {
    if (this.map.gmapRef) {
      this.$options.marker = null;
      this.$options.infoWindow = null;
    }
  },
  beforeDestroy() {
    this.resetAlertMapDisplay();
    this.updateShowMarkers(true);
    this.setMapBounds();
  },
  methods: {
    ...mapActions("map", ["setMapBounds"]),
    ...mapMutations("map", ["updateShowMarkers"]),
    ...mapMutations("navigation", ["updateShowSheet"]),
    beforeClose() {
      this.$emit("close");
    },
    showTooltip() {
      tooltipService.showTooltip("alerts");
      try {
        this.$gtag.event("tooltip_vehicle_alerts");
        this.$gtag.event("tooltip");
      } catch (err) {}
    },
    getDate(dateTime) {
      return dateTime.split("T")[0];
    },
    getTime(dateTime) {
      return dateTime.split("T")[1];
    },
    showModal(screenName) {
      modalService.showModal(this, screenName);
    },
    getAlertsByDate() {
      this.processing = true;
      const vehicleId = this.getActiveSafeAndSoundVehicleId();
      assetService
        .getVehicleAlertsHistoryByDate(vehicleId, this.fromDate, this.toDate)
        .then((data) => {
          this.clearAlertsState();
          this.searchResults = true;
          this.alerts = data;
          this.processing = false;
        })
        .catch((error) => {
          this.processing = false;
        });
    },
    getAlertIconURL(category) {
      let url = "";
      switch (category) {
        case "Acceleration":
          url = require("@/assets/images/trips/harsh-acceleration.svg");
          break;
        case "Braking":
          url = require("@/assets/images/trips/harsh-braking.svg");
          break;
        case "Cornering":
          url = require("@/assets/images/trips/harsh-cornering.svg");
          break;
        case "Geo-Fencing:EnterGeoZone":
        case "Geo-Fencing:ExitGeoZone":
        case "Geo-Fencing:IgnitionOff":
        case "Geo-Fencing:IgnitionOn":
          url = require("@/assets/images/trips/geo-zone.svg");
          break;
        case "Impacts":
          url = require("@/assets/images/trips/impacts.svg");
          break;
        case "Lane-Change":
          url = require("@/assets/images/trips/lane-changing.svg");
          break;
        case "OverSpeeding":
        case "Speeding":
          url = require("@/assets/images/trips/over-speeding.svg");
          break;
        case "Time Rule:IgnitionOn":
          url = require("@/assets/images/trips/time-rule.svg");
          break;
      }
      return url;
    },
    getAlertMapIconURL(category) {
      let url = "";
      switch (category) {
        case "Acceleration":
          url = require("@/assets/images/trips/harsh-acceleration_map.svg");
          break;
        case "Braking":
          url = require("@/assets/images/trips/harsh-braking_map.svg");
          break;
        case "Cornering":
          url = require("@/assets/images/trips/harsh-cornering_map.svg");
          break;
        case "Geo-Fencing:EnterGeoZone":
        case "Geo-Fencing:ExitGeoZone":
        case "Geo-Fencing:IgnitionOff":
        case "Geo-Fencing:IgnitionOn":
          url = require("@/assets/images/trips/geo-zone_map.svg");
          break;
        case "Impacts":
          url = require("@/assets/images/trips/impacts_map.svg");
          break;
        case "Lane-Change":
          url = require("@/assets/images/trips/lane-changing_map.svg");
          break;
        case "OverSpeeding":
        case "Speeding":
          url = require("@/assets/images/trips/over-speeding_map.svg");
          break;
        case "Time Rule:IgnitionOn":
          url = require("@/assets/images/trips/time-rule_map.svg");
          break;
      }
      return url;
    },
    getAlertIconSize(category) {
      if (category === "Time Rule:IgnitionOn") {
        return 30;
      }
      return 40;
    },
    displayAlertOnMap(key, alert) {
      if (this.activeAlertId !== key) {
        this.activeAlertId = key;
        this.updateShowMarkers(false);
        this.resetAlertMapDisplay();

        const position = new google.maps.LatLng(alert.Location.Latitude, alert.Location.Longitude);

        const bounds = new google.maps.LatLngBounds();
        bounds.extend(position);

        const icon = {
          url: this.getAlertMapIconURL(alert.Category),
          scaledSize: new google.maps.Size(50, 50),
          origin: new google.maps.Point(0, 0),
          anchor: new google.maps.Point(25, 25),
        };

        this.$options.marker = new google.maps.Marker({
          position: position,
          map: this.map.gmapRef.$mapObject,
          icon: icon,
          title: "",
        });

        const content = `
        <div class="alert-content">
          <table style="width: 300px;">
            <tbody>
              <tr>
                <td style="font-weight: bold;">Date: </td>
                <td>${alert.Location.DateTime.replace("T", " ")}</td>
              </tr>
              <tr>
                <td style="font-weight: bold;">Speed: </td>
                <td>${alert.Location.Speed} kph</td>
              </tr>
              <tr>
                <td style="font-weight: bold;">Heading:  </td>
                <td>${alert.Location.Heading}</td>
              </tr>
              <tr>
                <td style="font-weight: bold;">GPS Coordinates: </td>
                <td>${alert.Location.Latitude}, ${alert.Location.Longitude}</td>
              </tr>
              <tr>
                <td rolspan="2" style="font-weight: bold;">Address: </td>
                <td>${alert.Location.GeoAddress}</td>
              </tr>
            </tbody>
          </table>
        </div>
        `;

        this.$options.infoWindow = new google.maps.InfoWindow({
          content: content,
        });

        this.$options.infoWindow.open(this.map.gmapRef.$mapObject, this.$options.marker);

        this.$options.marker.addListener("click", () => {
          this.$options.infoWindow.open(this.map.gmapRef.$mapObject, this.$options.marker);
        });

        this.map.gmapRef.fitBounds(bounds);
        google.maps.event.addListenerOnce(this.map.gmapRef.$mapObject, "idle", () => {
          const zoomLevel = this.$isMobile ? 13 : 16;
          this.map.gmapRef.$mapObject.setZoom(zoomLevel);
        });

        if (this.$isMobile) {
          this.updateShowSheet(false);
        }
      }
    },
    resetAlertMapDisplay() {
      if (this.$options.marker) {
        this.$options.marker.setMap(null);
      }

      this.$options.marker = null;
    },
    clearAlertsState() {
      this.resetAlertMapDisplay();
      this.activeAlertId = null;
      this.searchResults = false;
    },
  },
  watch: {
    activeAssetId() {
      this.processing = true;
      const vehicleId = this.getActiveSafeAndSoundVehicleId();
      assetService
        .getVehicleAlertsHistory(vehicleId)
        .then((data) => {
          this.clearAlertsState();
          this.alerts = data;
          this.processing = false;
        })
        .catch((error) => {
          this.processing = false;
        });
    },
  },
};
</script>